import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Appointment } from '../../models/appointment.model';
import { Order } from '../../orders/models/order.models';

type CancellableItem = Appointment | Order;

/**
 * Modal component that allows users to choose or enter a reason for cancelling a cancellable item and submit or close the modal.
 * @component
 * @selector app-cancellation-reason-modal
 * @example
 *   <app-cancellation-reason-modal
 *     [isVisible]="true"
 *     [item]="selectedItem"
 *     (submit)="handleCancelSubmit($event)"
 *     (close)="handleModalClose()">
 *   </app-cancellation-reason-modal>
 * @input boolean isVisible - Controls whether the modal is visible. When true the modal opens and the form is reset.
 * @input CancellableItem|null item - The item that can be cancelled (e.g., booking or lot). If null, submission is prevented.
 * @output EventEmitter<{item: CancellableItem, reason: string}> submit - Emitted when the user confirms cancellation with a valid reason. Payload contains the item and the final reason string.
 * @output EventEmitter<void> close - Emitted when the modal is dismissed without submitting (close/cancel action).
 *
 * Purpose:
 *   Provides a user interface to select one of several predefined cancellation reasons or enter a custom "Other" reason,
 *   validates input (including a 300-character limit for custom reasons), and emits structured events for submit and close.
 *
 * Key properties:
 *   - selectedReason: string - currently selected predefined reason.
 *   - otherReason: string - free-text reason when "Other" is selected.
 *   - isOtherSelected: boolean - whether the "Other" option is active.
 *   - cancellationReasons: string[] - array of available predefined reasons (includes "Other").
 *
 * Key methods:
 *   - onReasonSelect(reason: string): void
 *       Sets the selected reason, toggles isOtherSelected when "Other" is chosen, and clears otherReason when switching away from "Other".
 *   - onSubmit(): void
 *       Validates the selection and free-text input (if "Other" is selected). If valid and an item is present, emits the submit event with { item, reason }.
 *   - onClose(): void
 *       Emits the close event to notify the parent to hide the modal.
 *   - resetForm(): void
 *       Clears selectedReason, otherReason, and isOtherSelected to restore the initial state.
 *
 * Useful getters / validations:
 *   - isSubmitDisabled: boolean
 *       True when no reason is selected, when "Other" is selected but empty, or when the free-text exceeds 300 characters.
 *   - isCharacterLimitExceeded: boolean
 *       True when otherReason length > 300.
 *   - exceededCharacters: number
 *       Number of characters beyond the 300-character limit for otherReason.
 *   - isOtherReasonEmpty: boolean
 *       True when "Other" is selected but the otherReason is empty or whitespace.
 *
 * Lifecycle hooks:
 *   - ngOnChanges(changes: SimpleChanges): void
 *       Watches the isVisible input and calls resetForm() whenever the modal is opened (isVisible becomes true) to ensure a fresh state.
 */
@Component({
  selector: 'app-cancellation-reason-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './cancellation-reason-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './cancellation-reason-modal.component.scss',
})
export class CancellationReasonModalComponent implements OnChanges {
  @Input() isVisible = false;
  @Input() item: CancellableItem | null = null;
  @Output() submit = new EventEmitter<{ item: CancellableItem; reason: string }>();
  @Output() close = new EventEmitter<void>();

  selectedReason = '';
  otherReason = '';
  isOtherSelected = false;

  cancellationReasons = [
    'Change of plans',
    'Flight cancellation/delay',
    'Safety concerns',
    'Technical problems after booking',
    'Issue with the lot',
    'Found a better lot',
    'QR code not working',
    'Personal emergency',
    'Other',
  ];

  onReasonSelect(reason: string): void {
    this.selectedReason = reason;
    this.isOtherSelected = reason === 'Other';
    if (!this.isOtherSelected) {
      this.otherReason = '';
    }
  }

  /**
   * Handle submission of the cancellation reason: validates the selected or "other" reason and emits a submit event with the item and final reason.
   * @example
   * nSubmit()
   * // Emits: { item: <Item>, reason: 'Final reason string' }
   * @returns {void} No return value; emits a submit event when validation passes.
   **/
  onSubmit(): void {
    if (!this.item) return;

    const finalReason = this.isOtherSelected ? this.otherReason : this.selectedReason;

    if (!finalReason.trim()) {
      // Show error or validation message
      return;
    }

    this.submit.emit({
      item: this.item,
      reason: finalReason,
    });
  }

  onClose(): void {
    this.close.emit();
  }

  get isSubmitDisabled(): boolean {
    if (!this.selectedReason) return true;
    if (this.isOtherSelected && !this.otherReason.trim()) return true;
    if (this.isOtherSelected && this.otherReason.length > 300) return true;
    return false;
  }

  get isCharacterLimitExceeded(): boolean {
    return this.isOtherSelected && this.otherReason.length > 300;
  }

  get exceededCharacters(): number {
    return this.isOtherSelected ? Math.max(0, this.otherReason.length - 300) : 0;
  }

  get isOtherReasonEmpty(): boolean {
    return this.isOtherSelected && !this.otherReason.trim();
  }

  resetForm(): void {
    this.selectedReason = '';
    this.otherReason = '';
    this.isOtherSelected = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && changes['isVisible'].currentValue === true) {
      this.resetForm();
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
